import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Lazily loads the Moneris Checkout script once per page session.
 */
@Injectable({ providedIn: 'root' })
export class MonerisCheckoutLoaderService {
  private loadPromise: Promise<void> | null = null;

  /**
   * Ensures `window.monerisCheckout` is available by injecting the configured script URL.
   * A failed load is not cached, so a failed Review preload never blocks the later checkout.
   */
  loadScript(): Promise<void> {
    if (typeof window !== 'undefined' && window.monerisCheckout) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<void>((resolve, reject) => {
      if (typeof document === 'undefined') {
        reject(new Error('Moneris Checkout requires a browser document.'));
        return;
      }

      const existing = document.querySelector<HTMLScriptElement>(
        `script[data-moneris-checkout="true"]`,
      );
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => this.failLoad(existing, reject));
        if (window.monerisCheckout) {
          resolve();
        }
        return;
      }

      const script = document.createElement('script');
      script.src = environment.monerisCheckoutScriptUrl;
      script.async = true;
      script.dataset['monerisCheckout'] = 'true';
      script.onload = () => resolve();
      script.onerror = () => this.failLoad(script, reject);
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  /**
   * Drops the failed script tag and cached promise so the next call injects a fresh script.
   */
  private failLoad(script: HTMLScriptElement, reject: (reason: Error) => void): void {
    script.remove();
    this.loadPromise = null;
    reject(new Error('Moneris Checkout script failed to load.'));
  }
}
